/**
 * paragraphCodeMode.ts — QA/Implementation paragraph config code overlay.
 *
 * When active, getContent() returns the raw paragraph config code wrapped in
 * guillemets (e.g. «PPNavDashboard») instead of the resolved text. This lets
 * QE and implementation teams instantly identify which DB entry drives each
 * string on screen.
 *
 * Activation
 * ----------
 * URL parameter:  ?pp_codes=1  (persists to localStorage)
 * Deactivation:   ?pp_codes=0  (clears localStorage)
 * Toggle:         call toggleParagraphCodeMode()
 *
 * The URL param is processed once on app load (see bootstrapParagraphCodeMode).
 * After that the localStorage flag drives the mode so it persists across
 * navigation and page refreshes within the same session.
 */

const STORAGE_KEY = 'DisplayParagraphConfigMapping';
const PARAM_KEY   = 'DisplayParagraphConfigMapping';

/** Returns true when paragraph-code overlay is active. */
export function isParagraphCodeMode(): boolean {
  return localStorage.getItem(STORAGE_KEY) === '1';
}

/** Enable or disable the mode programmatically. */
export function setParagraphCodeMode(enabled: boolean): void {
  if (enabled) {
    localStorage.setItem(STORAGE_KEY, '1');
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  // Dispatch a storage event so any active useContent instances can re-render.
  window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY }));
}

/** Toggle the mode and return the new state. */
export function toggleParagraphCodeMode(): boolean {
  const next = !isParagraphCodeMode();
  setParagraphCodeMode(next);
  return next;
}

/**
 * Read ?pp_codes= from the current URL and apply it to localStorage.
 * Call once at app startup (before React renders).
 */
export function bootstrapParagraphCodeMode(): void {
  const params = new URLSearchParams(window.location.search);
  const param  = params.get(PARAM_KEY);
  if (param === '1') {
    setParagraphCodeMode(true);
  } else if (param === '0') {
    setParagraphCodeMode(false);
  }
}

/**
 * Wrap a paragraph config code for display in overlay mode.
 * Uses guillemets so the codes are visually distinct from normal text
 * and are unlikely to collide with real content.
 */
export function wrapCode(code: string): string {
  return `«${code}»`;
}
